// src/pages/PublicBookingPage.jsx
// Es la Card del médico al escoger consulta
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
  Button,
  Rating,
  Tooltip,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  LocalHospital as HospitalIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  CalendarMonth as CalendarIcon,
  VerifiedUser as VerifiedIcon,
  Language as LanguageIcon,
  School as EducationIcon,
  AccessTime as TimeIcon,
  LocalPhone as PhoneIcon,
  Share as ShareIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Audiotrack as TikTokFallbackIcon,
} from '@mui/icons-material';
import axiosInstance from '../axiosInstance';
import PublicCalendar from '../components/PublicCalendar';
import BookingForm from '../components/BookingForm';
import GuestBookingForm from '../components/GuestBookingForm';

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', MXN: '$', HNL: 'L', GTQ: 'Q', CRC: '₡', NIO: 'C$',
  PAB: 'B/.', DOP: 'RD$', ARS: '$', CLP: '$', COP: '$', PEN: 'S/',
  BOB: 'Bs', BRL: 'R$', UYU: '$U', PYG: '₲', VES: 'Bs.', GBP: '£',
};

const getCurrencySymbol = (code) => CURRENCY_SYMBOLS[(code || '').toUpperCase()] || '';
const formatMoney = (amount, code) => {
  const sym = getCurrencySymbol(code);
  const val = Number(amount ?? 0);
  const formatted = (val % 1 === 0 ? val.toFixed(0) : val.toFixed(2));
  return `${sym ? sym + ' ' : ''}${formatted} ${code || ''}`.trim();
};

const PublicBookingPage = () => {
  const { doctorId: doctorKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(false);
  const [error, setError] = useState(null);

  const [resolvedId, setResolvedId] = useState(null);
  const [reviewsSummary, setReviewsSummary] = useState({ average: 0, total: 0, distribution: { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 } });
  const [reviews, setReviews] = useState([]);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsCount, setReviewsCount] = useState(0);
  const [myName, setMyName] = useState('');
  const [myEmail, setMyEmail] = useState('');
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState('');
  const [postSuccess, setPostSuccess] = useState('');
  const [patientProfile, setPatientProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);


  const [slotDurationMin, setSlotDurationMin] = useState(null);

  // Tokens
  const patientToken = localStorage.getItem('patientToken');
  const isGuest = localStorage.getItem('isGuest') === 'true';

  // Gating por ruta:
  // - Rutas que empiezan por /patient => zona paciente (BookingForm si hay token y no invitado)
  // - Cualquier otra (ej /book/:id) => SIEMPRE GuestBookingForm (aunque exista patientToken)
  const isPatientRoute = location.pathname.startsWith('/patient');
  const isPatient = isPatientRoute && !!patientToken && !isGuest;

  // Diálogo de reserva
  const [openBookingDialog, setOpenBookingDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [processing, setProcessing] = useState(false);

  const phoneLabel = [doctor?.codigo_pais_consultorio?.trim(), doctor?.telefono_consultorio?.trim()]
    .filter(Boolean)
    .join(' ');

  const fetchSlotDuration = async (id) => {
    try {
      const start = new Date().toISOString();
      const end = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await axiosInstance.get(`/doctors/${id}/available-slots/`, { params: { start, end } });
      const slots = Array.isArray(data) ? data : (data?.slots || []);
      if (!slots.length) { setSlotDurationMin(null); return; }
      const first = slots[0];
      const minutes = typeof first.duration === 'number'
        ? first.duration
        : Math.round((new Date(first.end) - new Date(first.start)) / 60000);
      setSlotDurationMin(minutes);
    } catch {
      setSlotDurationMin(null);
    }
  };

  const stripAt = (v) => (typeof v === 'string' && v.trim().startsWith('@') ? v.trim().slice(1) : (v || ''));
  const buildSocialUrl = (key, raw) => {
    if (!raw) return '';
    let v = String(raw).trim();
    if (/^https?:\/\//i.test(v)) return v;
    if (v.includes('.')) return `https://${v}`;
    const h = stripAt(v);
    switch (key) {
      case 'instagram': return `https://instagram.com/${h}`;
      case 'facebook': return `https://facebook.com/${h}`;
      case 'tiktok': return v.includes('tiktok.com') ? `https://${v}` : `https://tiktok.com/@${h}`;
      case 'linkedin': return v.includes('linkedin.com') ? `https://${v}` : `https://www.linkedin.com/in/${h}`;
      default: return v;
    }
  };

  const idiomas = Array.isArray(doctor?.idiomas)
    ? Array.from(new Set(doctor.idiomas.filter(Boolean)))
    : [];

  const isNumericId = (v) => /^[0-9]+$/.test(String(v || ''));
  const effectiveDoctorId = isNumericId(doctorKey) ? doctorKey : resolvedId;

  // Carga de información pública del médico
  const loadDoctor = async (idToUse) => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(`/doctors/${idToUse}/public/`);
      setDoctor(data);
      setError('');
      setTimeout(() => setFadeIn(true), 50);
    } catch (e) {
      if (e?.response?.status === 404) {
        setError('El enlace amigable no existe. Verifica el nombre o usa el enlace con ID.');
      } else {
        setError('No se pudo resolver el enlace amigable del médico.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadReviewsSummary = async (id) => {
    try {
      const { data } = await axiosInstance.get(`/doctors/${id}/reviews/summary/`);
      setReviewsSummary(data);
    } catch {}
  };

  const loadReviews = async (id, page = 1) => {
    try {
      const { data } = await axiosInstance.get(`/doctors/${id}/reviews/`, { params: { page, page_size: 5 } });
      setReviews(data.results || []);
      setReviewsCount(data.count || 0);
    } catch {}
  };

  // Cargar perfil del paciente para autocompletar nombre/email y ocultar inputs
  useEffect(() => {
    let active = true;
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true);
        const { data } = await axiosInstance.get('/patients/profile/');
        if (!active) return;
        setPatientProfile(data || null);
        // Prefill si no se habían llenado aún
        const fullName = `${data?.first_name || ''} ${data?.last_name || ''}`.trim() || 'Paciente';
        if (!myName) setMyName(fullName);
        if (!myEmail && data?.email) setMyEmail(data.email);
      } catch {
        if (!active) return;
        // Si falla, mantenemos los campos vacíos
      } finally {
        if (active) setLoadingProfile(false);
      }
    };
    if (isPatient) fetchProfile();
    return () => { active = false; };
  }, [isPatient]); 

  // Publicar opinión
  const handlePostReview = async () => {
    setPostError('');
    setPostSuccess('');

    const derivedName = `${patientProfile?.first_name || ''} ${patientProfile?.last_name || ''}`.trim() || 'Paciente';
    const payloadName = isPatient ? (myName?.trim() || derivedName) : myName.trim();
    const payloadEmail = isPatient ? (myEmail?.trim() || patientProfile?.email || '') : myEmail.trim();

    // Validaciones (no mostrar inputs para pacientes, pero aún validar rating)
    if (!isPatient && !payloadName) { setPostError('Ingresa tu nombre.'); return; }
    if (!isPatient && !payloadEmail) { setPostError('Ingresa tu email.'); return; }
    if (!myRating || myRating < 1) { setPostError('Selecciona una calificación.'); return; }
    if (!effectiveDoctorId) return;

    try {
      setPosting(true);
      await axiosInstance.post(`/doctors/${effectiveDoctorId}/reviews/`, {
        nombre: payloadName,
        email: payloadEmail, 
        comentario: myComment.trim(),
        rating: myRating,
      });
      setPostSuccess('¡Gracias! Tu opinión quedó pendiente de aprobación.');
      if (!isPatient) { setMyName(''); setMyEmail(''); }
      setMyRating(0);
      setMyComment('');
      setReviewsPage(1);
      await loadReviews(effectiveDoctorId, 1);
      await loadReviewsSummary(effectiveDoctorId);
    } catch (e) {
      setPostError(e?.response?.data?.detail || e?.response?.data?.error || 'No se pudo enviar tu opinión.');
    } finally {
      setPosting(false);
    }
  };        
      
  // Resolver doctorId si viene como slug
  useEffect(() => {
    if (!doctorKey) return;
    if (isNumericId(doctorKey)) {
      setResolvedId(doctorKey);
      loadDoctor(doctorKey);
      loadReviewsSummary(doctorKey);
      loadReviews(doctorKey, 1);
      fetchSlotDuration(doctorKey);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const { data } = await axiosInstance.get('/doctors/resolve-slug/', { params: { slug: doctorKey } });
        const id = data?.id;
        if (id) {
          setResolvedId(id);
          navigate(`/book/${id}`, { replace: true });
          await loadDoctor(id);
          await loadReviewsSummary(id);
          await loadReviews(id, 1);
          await fetchSlotDuration(id);
        } else {
          setError('No se pudo resolver el enlace amigable del médico.');
        }
      } catch (e) {
        console.error(e);
        setError('No se pudo resolver el enlace amigable del médico.');
      } finally {
        setLoading(false);
      }
    })();
  }, [doctorKey]);

  // Permite abrir el formulario si la URL trae ?start&end
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const start = params.get('start');
    if (start) {
      const end = params.get('end');
      const slot = {
        start: new Date(start),
        end: end ? new Date(end) : new Date(new Date(start).getTime() + (slotDurationMin || doctor?.duracion_cita_default || 30) * 60000),
      };
      setSelectedSlot(slot);
      setOpenBookingDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, slotDurationMin, doctor?.duracion_cita_default]);

  // Handlers de Diálogo/Formulario
  const handleOpenBooking = (slot) => {
    let s = slot?.start instanceof Date ? slot.start : new Date(slot?.start);
    let e = slot?.end instanceof Date ? slot.end : new Date(slot?.end);
    if (!e || Number.isNaN(e.getTime())) {
      const mins = slotDurationMin || doctor?.duracion_cita_default || 30;
      e = new Date(s.getTime() + mins * 60000);
    }
    setSelectedSlot({ start: s, end: e, rawStart: slot?.rawStart, rawEnd: slot?.rawEnd });
    setOpenBookingDialog(true);
  };

  const handleCloseBooking = () => {
    setOpenBookingDialog(false);
    setSelectedSlot(null);
  };

  const handleProcessingChange = (flag) => setProcessing(!!flag);

  const handleBookingSuccess = () => {
    setOpenBookingDialog(false);
    setSelectedSlot(null);
    navigate('/thank-you');
  };

  const socialItems = [
    { key: 'instagram', label: 'Instagram', raw: doctor?.instagram, icon: <InstagramIcon />, brand: '#E1306C' },
    { key: 'facebook',  label: 'Facebook',  raw: doctor?.facebook,  icon: <FacebookIcon />,  brand: '#1877F2' },
    { key: 'tiktok',    label: 'TikTok',    raw: doctor?.tiktok,    icon: <TikTokFallbackIcon />, brand: '#000000' },
    { key: 'linkedin',  label: 'LinkedIn',  raw: doctor?.linkedin,  icon: <LinkedInIcon />,  brand: '#0A66C2' },
  ]
  .map(s => ({ ...s, url: buildSocialUrl(s.key, s.raw) }))
  .filter(s => !!s.url);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', gap: 3, bgcolor: theme.palette.background.default }}>
        <CircularProgress size={70} thickness={4} sx={{ color: theme.palette.primary.main, '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500, animation: 'pulse 1.5s infinite ease-in-out', '@keyframes pulse': { '0%': { opacity: 0.6 }, '50%': { opacity: 1 }, '100%': { opacity: 0.6 } } }}>
          Cargando información del médico...
        </Typography>
      </Box>
    );
  }

  if (error || !doctor) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 4, textAlign: 'center', maxWidth: 600, mx: 'auto', border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`, '&:hover': { boxShadow: `0 8px 24px ${alpha(theme.palette.error.main, 0.15)}` }, transition: 'box-shadow 0.3s ease' }}>
          <Alert severity="error" variant="filled" sx={{ mb: 3, borderRadius: 3, '& .MuiAlert-icon': { fontSize: '2rem' } }}>
            {error || 'No se encontró el médico solicitado'}
          </Alert>
          <Typography variant="body1" paragraph>
            Lo sentimos, no pudimos encontrar la información solicitada. Por favor, verifica el enlace o intenta nuevamente más tarde.
          </Typography>
          <Button variant="contained" color="primary" size="large" onClick={() => window.location.reload()} sx={{ mt: 2, borderRadius: 3, px: 4, py: 1.5, fontWeight: 'bold', boxShadow: theme.shadows[4], '&:hover': { transform: 'translateY(-3px)', boxShadow: theme.shadows[8] }, transition: 'transform 0.3s ease, box-shadow 0.3s ease' }}>
            Reintentar
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        minHeight: '100vh',
        py: { xs: 2, md: 4 },
        backgroundImage: `radial-gradient(${alpha(theme.palette.primary.main, 0.05)} 1px, transparent 1px)`,
        backgroundSize: '20px 20px'
      }}
    >
      <Container maxWidth="lg" disableGutters sx={{ px: { xs: 1, md: 3 } }}>
        <Fade in={fadeIn} timeout={800}>
          <Grid container spacing={4}>
            {/* Columna info médico */}
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 0,
                  borderRadius: 4,
                  position: 'sticky',
                  top: 20,
                  overflow: 'hidden',
                  boxShadow: theme.shadows[3],
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': { transform: 'translateY(-5px)', boxShadow: theme.shadows[6] }
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    p: 3,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': { content: '""', position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%', background: `radial-gradient(circle, ${alpha('#ffffff', 0.1)} 0%, transparent 60%)`, opacity: 0.6 }
                  }}
                >
                  <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                    <Avatar
                      src={doctor.foto_perfil}
                      alt={doctor.nombre_completo}
                      sx={{ width: 130, height: 130, margin: '0 auto', mb: 2, border: '4px solid white', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', transition: 'transform 0.3s ease', '&:hover': { transform: 'scale(1.05)' } }}
                    >
                      {doctor.nombre_completo?.charAt(0)}
                    </Avatar>
                    <Typography variant="h4" gutterBottom fontWeight="bold" color="white" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.2)', mb: 1 }}>
                      {doctor.nombre_completo}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1 }}>
                      <Chip icon={<HospitalIcon sx={{ color: 'white !important' }} />} label={doctor.especialidad_principal} sx={{ bgcolor: alpha(theme.palette.common.white, 0.2), color: 'white', fontWeight: 'bold', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.3) }, transition: 'background-color 0.3s ease' }} />
                      <Tooltip title="Médico verificado" arrow>
                        <Chip icon={<VerifiedIcon sx={{ color: 'white !important' }} />} label="Verificado" sx={{ bgcolor: alpha(theme.palette.success.main, 0.7), color: 'white', fontWeight: 'bold', '&:hover': { bgcolor: theme.palette.success.main }, transition: 'background-color 0.3s ease' }} />
                      </Tooltip>
                    </Stack>
                    {doctor?.pais?.trim() && (
                      <Stack direction="row" justifyContent="center" sx={{ mb: 1 }}>
                        <Chip icon={<LocationIcon sx={{ color: 'white !important' }} />} label={doctor.pais.trim()} sx={{ bgcolor: alpha(theme.palette.common.white, 0.2), color: 'white', fontWeight: 'bold', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.3) }, transition: 'background-color 0.3s ease' }} />
                      </Stack>
                    )}
                    {doctor.sub_especialidad && (
                      <Typography variant="body2" color="white" sx={{ opacity: 0.9, fontStyle: 'italic' }}>
                        {doctor.sub_especialidad}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ p: 3 }}>
                  {/* Calificación */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 3 }}>
                    <Rating value={Number(reviewsSummary.average) || 0} precision={0.1} readOnly sx={{ color: theme.palette.warning.main, '& .MuiRating-iconFilled': { filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' } }} />
                    <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                      {Number(reviewsSummary.average || 0).toFixed(1)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({reviewsSummary.total} opiniones)
                    </Typography>
                  </Box>

                  {/* Sobre el médico */}
                  {doctor.sobre_mi && (
                    <Box sx={{ mb: 3, p: 2, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, transition: 'all 0.3s ease', '&:hover': { bgcolor: alpha(theme.palette.background.paper, 0.5), boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}` } }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', color: theme.palette.primary.main }}>
                        <EducationIcon sx={{ mr: 1 }} /> Sobre el médico
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, textAlign: 'justify' }}>
                        {doctor.sobre_mi}
                      </Typography>
                    </Box>
                  )}

                  {/* Información adicional */}
                  <Stack spacing={2}>
                    {/* Idiomas */}
                    <Card variant="outlined" sx={{ borderRadius: 3, transition: 'all 0.3s ease', '&:hover': { boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`, borderColor: theme.palette.primary.main } }}>
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                            <LanguageIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" gutterBottom fontWeight="bold">Idiomas</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {idiomas.length > 0 ? (
                                idiomas.map((lang, idx) => (
                                  <Chip key={`${lang}-${idx}`} label={lang} size="small" sx={{ m: 0.5, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) } }} />
                                ))
                              ) : (
                                <Chip label="No especificado" variant="outlined" size="small" sx={{ m: 0.5 }} />
                              )}
                            </Stack>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>

                    {/* Contacto */}
                    <Card variant="outlined" sx={{ borderRadius: 3, transition: 'all 0.3s ease', '&:hover': { boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`, borderColor: theme.palette.primary.main } }}>
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                            <PhoneIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" gutterBottom fontWeight="bold">Contacto del consultorio</Typography>
                            <Stack spacing={0.5}>
                              <Typography variant="body2" color="text.secondary"><strong>Teléfono:</strong> {phoneLabel || 'No especificado'}</Typography>
                              <Typography variant="body2" color="text.secondary"><strong>Dirección:</strong> {doctor.direccion_consultorio?.trim() || 'No especificada'}</Typography>
                            </Stack>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>

                    {/* Duración */}
                    <Card variant="outlined" sx={{ borderRadius: 3, transition: 'all 0.3s ease', '&:hover': { boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`, borderColor: theme.palette.primary.main } }}>
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                            <TimeIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" gutterBottom fontWeight="bold">Duración de consulta</Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                              <Chip
                                label={slotDurationMin ? `Duración típica: ${slotDurationMin} min` : `Duración típica: ${doctor?.duracion_cita_default || 30} min`}
                                color="primary"
                                variant="outlined"
                                size="small"
                                sx={{ fontWeight: 700 }}
                              />
                            </Stack>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>

                    {/* Redes sociales */}
                    {socialItems.length > 0 && (
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                          borderColor: theme.palette.primary.main
                        }
                      }}
                    >
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                            <ShareIcon />
                          </Avatar>
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                              Redes sociales
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {socialItems.map((s) => (
                                <Button
                                  key={s.key}
                                  component="a"
                                  href={s.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  startIcon={s.icon}
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    m: 0.5,
                                    borderRadius: 20,
                                    fontWeight: 700,
                                    color: s.brand || theme.palette.text.primary,
                                    borderColor: alpha(s.brand || theme.palette.primary.main, 0.4),
                                    '&:hover': {
                                      borderColor: s.brand || theme.palette.primary.main,
                                      boxShadow: `0 6px 16px ${alpha(s.brand || theme.palette.primary.main, 0.2)}`
                                    }
                                  }}
                                >
                                  {s.label}
                                </Button>
                              ))}
                            </Stack>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                    {/* Precio */}
                    <Card sx={{ borderRadius: 3, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, transition: 'all 0.3s ease', '&:hover': { boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.2)}`, transform: 'translateY(-3px)' } }}>
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                          <Avatar sx={{ bgcolor: theme.palette.primary.main, color: 'white', width: 56, height: 56, boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` }}>
                            <MoneyIcon fontSize="large" />
                          </Avatar>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary" fontWeight="medium">Precio de consulta</Typography>
                            <Typography variant="h4" color="primary.main" fontWeight="bold" sx={{ textShadow: `0 1px 2px ${alpha(theme.palette.primary.main, 0.2)}`, mt: 0.5 }}>
                              {formatMoney(doctor?.tarifa_consulta, doctor?.moneda || 'USD')}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Stack>

                  {/* Opiniones de pacientes */}
                  <Card
                    id="opiniones"
                    variant="outlined"
                    sx={{
                      mt: 3,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.12)}`,
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                      }
                    }}
                  >
                    <CardContent>
                      {/* Encabezado con resumen */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Opiniones de pacientes
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          <Rating value={Number(reviewsSummary.average) || 0} precision={0.1} readOnly />
                          <Typography variant="body2" fontWeight={700}>
                            {(Number(reviewsSummary.average) || 0).toFixed(1)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ({Number(reviewsSummary.total) || 0} opiniones)
                          </Typography>
                        </Stack>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                          {/* Formulario para dejar opinión */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom fontWeight="bold">Deja tu opinión</Typography>
                            {/* Si es paciente autenticado, no mostramos nombre y email */}
                            {isPatient ? (
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <Avatar sx={{ width: 28, height: 28 }}>
                                  {(patientProfile?.first_name || 'P').charAt(0).toUpperCase()}                               
                                </Avatar>
                                <Typography variant="body2" color="text.secondary">
                                   Opinión como: <strong>{(myName || `${patientProfile?.first_name || ''} ${patientProfile?.last_name || ''}`.trim() || 'Paciente')}</strong>
                                  {patientProfile?.email ? ` (${patientProfile.email})` : ''}
                                </Typography>
                              </Stack>
                            ) : (
                              // Invitados: sí pedimos nombre y email  
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                              <TextField
                                size="small"
                                label="Tu nombre"
                                value={myName}
                                onChange={(e) => setMyName(e.target.value)}
                              />
                              <TextField
                                size="small"
                                label="Tu email"
                                value={myEmail}
                                onChange={(e) => setMyEmail(e.target.value)}
                              />
                              </Stack>
                            )}  
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" color="text.secondary">Calificación:</Typography>
                                <Rating value={myRating} onChange={(_, v) => setMyRating(v || 0)} />
                                {loadingProfile && isPatient && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  Cargando perfil…
                                </Typography>  
                              )}  
                              </Stack>
                    
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              sx={{ mt: 1 }}
                              label="Comentario (opcional)"
                              placeholder="¿Qué te pareció la atención?"
                              value={myComment}
                              onChange={(e) => setMyComment(e.target.value)}
                            />
                            <Box sx={{ mt: 1 }}>
                              <Button variant="contained" onClick={handlePostReview} disabled={posting}>
                                {posting ? 'Enviando…' : 'Publicar'}
                              </Button>
                            </Box>
                            {!!postError && <Alert severity="error" sx={{ mt: 1 }}>{postError}</Alert>}
                            {!!postSuccess && <Alert severity="success" sx={{ mt: 1 }}>{postSuccess}</Alert>}
                          </Box>

                          {/* Listado de opiniones aprobadas */}
                      {Array.isArray(reviews) && reviews.length > 0 ? (
                        <>
                          <Stack spacing={2}>
                            {reviews.map((r) => (
                              <Box
                                key={r.id}
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`
                                }}
                              >
                                <Stack direction="row" spacing={2}>
                                  <Avatar
                                    sx={{
                                      bgcolor: theme.palette.primary.main,
                                      color: theme.palette.primary.contrastText,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {(r?.nombre || 'P').toString().charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                      <Typography variant="subtitle2" fontWeight="bold" noWrap title={r?.nombre}>
                                        {r?.nombre}
                                      </Typography>
                                      <Rating value={Number(r?.rating) || 0} readOnly size="small" />
                                      <Typography variant="caption" color="text.secondary">
                                        {r?.created_at ? new Date(r.created_at).toLocaleString() : ''}
                                      </Typography>
                                    </Stack>
                                    {!!r?.comentario && (
                                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        {r?.comentario}
                                      </Typography>
                                    )}
                                  </Box>
                                </Stack>
                              </Box>
                            ))}
                          </Stack>

                          {/* Navegación simple (Anterior / Siguiente) */}
                          {reviewsCount > 5 && (
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  const p = Math.max(1, reviewsPage - 1);
                                  setReviewsPage(p);
                                  if (effectiveDoctorId) loadReviews(effectiveDoctorId, p);
                                }}
                                disabled={reviewsPage <= 1}
                              >
                                Anterior
                              </Button>

                              <Typography variant="body2" color="text.secondary">
                                Página {reviewsPage} de {Math.max(1, Math.ceil((reviewsCount || 0) / 5))}
                              </Typography>

                              <Button
                                variant="outlined"
                                onClick={() => {
                                  const totalPages = Math.max(1, Math.ceil((reviewsCount || 0) / 5));
                                  const p = Math.min(totalPages, reviewsPage + 1);
                                  setReviewsPage(p);
                                  if (effectiveDoctorId) loadReviews(effectiveDoctorId, p);
                                }}
                                disabled={reviewsPage >= Math.max(1, Math.ceil((reviewsCount || 0) / 5))}
                              >
                                Siguiente
                              </Button>
                            </Stack>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Aún no hay opiniones aprobadas.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                  
                  <Alert severity="info" sx={{ mt: 3, borderRadius: 3, boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`, '& .MuiAlert-icon': { alignItems: 'center' }, transition: 'all 0.3s ease', '&:hover': { boxShadow: `0 6px 16px ${alpha(theme.palette.info.main, 0.25)}` } }}>
                    El pago se realiza de forma segura a través de PayPal al momento de agendar.
                  </Alert>
                </Box>
              </Paper>
            </Grid>

            {/* Calendario */}
            <Grid item xs={12} md={8} 
              sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: { xs: 'calc(100dvh - 200px)', md: 'auto' } // da más altura al calendario en mobile
            }}
          >
              <Paper elevation={0} sx={{ p: 0, borderRadius: { xs: 0, md: 4 }, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: theme.shadows[3], transition: 'transform 0.3s ease, box-shadow 0.3s ease', '&:hover': { boxShadow: theme.shadows[6] } }}>
                <Box 
                  sx={{
                    bgcolor: 'secondary.main',
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                    p: { xs: 2, md: 3 },            // <- menos padding en mobile
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '-50%',
                      left: '-50%',
                      width: '200%',
                      height: '200%',
                      background: `radial-gradient(circle, ${alpha('#ffffff', 0.1)} 0%, transparent 60%)`,
                      opacity: 0.6
                    }
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
                    <Avatar sx={{ bgcolor: 'white', color: 'secondary.main', width: 56, height: 56, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                      <CalendarIcon fontSize="large" />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="white" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.2)', lineHeight: 1.2 }}>
                        Selecciona una fecha y hora
                      </Typography>
                      <Typography variant="body1" color="white" sx={{ opacity: 0.9, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                        Elige un horario disponible para tu consulta con {doctor.nombre_completo}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box sx={{ p: 3 }}>
                  <Box
                    sx={{
                      height: { xs: 'calc(100dvh - 240px)', md: 'calc(100vh - 300px)' },
                      minHeight: { xs: 420, md: 600 },
                      borderRadius: 3,
                      overflow: 'hidden',
                      position: 'relative',
                      zIndex: 1,
                      '& .fc-event': { cursor: 'pointer !important', pointerEvents: 'auto !important' },
                      '& .fc-view-harness': { pointerEvents: 'auto !important' }
                    }}
                  >
                    {effectiveDoctorId ? (
                      <PublicCalendar doctorId={effectiveDoctorId} onSelectSlot={(slot) => handleOpenBooking(slot)} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">Preparando agenda del médico...</Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Fade>
      </Container>

      {/* Diálogo del formulario de reserva */}
      <Dialog open={openBookingDialog} onClose={processing ? undefined : handleCloseBooking} maxWidth="md" fullWidth>
        <DialogTitle>
          {isPatient ? 'Confirmar reserva' : 'Reservar cita como invitado'}
        </DialogTitle>
        <DialogContent dividers>
          {effectiveDoctorId && selectedSlot && (
            isPatient ? (
              <BookingForm
                doctorId={effectiveDoctorId}
                selectedSlot={selectedSlot}
                onSuccess={handleBookingSuccess}
                onCancel={handleCloseBooking}
                onProcessingChange={handleProcessingChange}
              />
            ) : (
              <GuestBookingForm
                doctorId={effectiveDoctorId}
                selectedSlot={selectedSlot}
                onSuccess={handleBookingSuccess}
                onCancel={handleCloseBooking}
                onProcessingChange={handleProcessingChange}
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBooking} disabled={processing}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PublicBookingPage;
